import React, { useState, useEffect } from 'react';
import './AboutOdonata.css';
import { Bug, Eye, Wind } from 'lucide-react';

const AboutOdonata = () => {
  const [dragonflyList, setDragonflyList] = useState([]);
  const [damselflyList, setDamselflyList] = useState([]);
  const [dIndex, setDIndex] = useState(0);
  const [zIndex, setZIndex] = useState(0);

  useEffect(() => {
    const fetchExamples = async () => {
      try {
        // Fetch Dragonfly options
        let dres = await fetch('https://api.inaturalist.org/v1/observations?user_id=gowthama_pandian&taxon_id=47927&per_page=10');
        let ddata = await dres.json();
        if (!ddata.results || ddata.results.length === 0) {
          dres = await fetch('https://api.inaturalist.org/v1/observations?taxon_id=47927&quality_grade=research&per_page=10');
          ddata = await dres.json();
        }
        setDragonflyList(ddata.results || []);

        // Fetch Damselfly options
        let zres = await fetch('https://api.inaturalist.org/v1/observations?user_id=gowthama_pandian&taxon_id=47928&per_page=10');
        let zdata = await zres.json();
        if (!zdata.results || zdata.results.length === 0) {
          zres = await fetch('https://api.inaturalist.org/v1/observations?taxon_id=47928&quality_grade=research&per_page=10');
          zdata = await zres.json();
        }
        setDamselflyList(zdata.results || []);

      } catch (err) {
        console.error("Error fetching comparison images:", err);
      }
    };
    fetchExamples();
  }, []);

  // Timer for Dragonfly cycling
  useEffect(() => {
    if (dragonflyList.length > 1) {
      const timer = setInterval(() => {
        setDIndex(prev => (prev + 1) % dragonflyList.length);
      }, 10000); // 10s interval
      return () => clearInterval(timer);
    }
  }, [dragonflyList]);

  // Timer for Damselfly cycling
  useEffect(() => {
    if (damselflyList.length > 1) {
      const timer = setInterval(() => {
        setZIndex(prev => (prev + 1) % damselflyList.length);
      }, 10000); // 10s interval
      return () => clearInterval(timer);
    }
  }, [damselflyList]);

  const currentDragonfly = dragonflyList[dIndex];
  const currentDamselfly = damselflyList[zIndex];

  return (
    <div className="about-odonata-page">
      <main className="container odonata-content">

        <section className="intro-section">
          <h2>The Order Odonata</h2>
          <p>
            Odonata is an order of carnivorous insects, encompassing dragonflies and damselflies. 
            They are characterized by their large multifaceted eyes, two pairs of strong transparent wings, 
            and elongated bodies. 
          </p>
        </section>

        <section className="comparison-section">
          <h2>Dragonfly vs. Damselfly</h2>
          <p className="subtitle">How to tell these two ancient neighbors apart:</p>
          
          <div className="comparison-grid">
            <div className="comp-card dragonfly">
              <div className="comp-image-wrapper">
                {currentDragonfly ? (
                   <img 
                     src={currentDragonfly.photos[0].url.replace('square', 'medium')} 
                     alt="Dragonfly example" 
                     className="cycle-image"
                   />
                ) : (
                  <div className="img-placeholder">Dragonfly Example</div>
                )}
              </div>
              <div className="comp-info">
                <h3>Dragonflies <span className="scientific">(Anisoptera)</span></h3>
                <ul>
                  <li><Bug size={18} /> <strong>Body:</strong> Usually thick and stocky.</li>
                  <li><Wind size={18} /> <strong>Wings:</strong> Held out horizontally when at rest. Back wings are wider.</li>
                  <li><Eye size={18} /> <strong>Eyes:</strong> Large and often touch at the top of the head.</li>
                </ul>
              </div>
            </div>

            <div className="comp-card damselfly">
              <div className="comp-image-wrapper">
                {currentDamselfly ? (
                   <img 
                     src={currentDamselfly.photos[0].url.replace('square', 'medium')} 
                     alt="Damselfly example" 
                     className="cycle-image"
                   />
                ) : (
                  <div className="img-placeholder">Damselfly Example</div>
                )}
              </div>
              <div className="comp-info">
                <h3>Damselflies <span className="scientific">(Zygoptera)</span></h3>
                <ul>
                  <li><Bug size={18} /> <strong>Body:</strong> Very slender and needle-like.</li>
                  <li><Wind size={18} /> <strong>Wings:</strong> Typically folded back over the body when at rest. Both pairs similar in shape.</li>
                  <li><Eye size={18} /> <strong>Eyes:</strong> Smaller and widely separated on the sides of the head.</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="lifecycle-section">
          <h2>Ancient Predators</h2>
          <p>
            They spend most of their lives underwater in their larval stage, where they are fierce aquatic hunters. 
            When they emerge as adults, they transform into the aerial acrobats we see today, capable of hovering 
            and flying backwards at impressive speeds.
          </p>
        </section>
      </main>
    </div>
  );
};

export default AboutOdonata;
